/**
 * Sticky header of the Dossier Detail page (Phase 1 UX).
 * Always visible while scrolling:
 *   - Block 1: client summary (nom · prénom · département · régie · partenaire financier · produit · régie · partenaire financier · sous-traitant · statut)
 *   - Block 2: horizontal workflow (6 phases: Conformité → … → Déblocage → SAV)
 *
 * Clicking a phase advances it (pending → in_progress → done → pending).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Check, Circle, PlayCircle, Lock, ShieldCheck, Banknote, FileSignature, Hammer, Wallet, LifeBuoy } from "lucide-react";
import StatusBadge from "./StatusBadge";
import { api } from "../lib/api";
import { DossierTagsEditor } from "./DossierTags";
import EquipeBlock from "./EquipeBlock";
import { CrmStatusSelect } from "./CrmStatus";

const PHASES = [
  { key: "conformite", label: "Conformité", icon: ShieldCheck },
  { key: "financement", label: "Financement", icon: Banknote },
  { key: "demarches", label: "Démarches", icon: FileSignature },
  { key: "installation", label: "Installation", icon: Hammer },
  { key: "deblocage", label: "Déblocage de fonds", icon: Wallet },
  { key: "sav", label: "Service après-vente", icon: LifeBuoy },
];

const STATUS_CYCLE = ["pending", "in_progress", "done"];
const STATUS_STYLES = {
  pending: { dot: "bg-norea-border", text: "text-norea-textMuted", bg: "bg-norea-surfaceHover", border: "border-norea-border", Icon: Circle },
  in_progress: { dot: "bg-amber-500", text: "text-amber-800", bg: "bg-amber-50", border: "border-amber-300", Icon: PlayCircle },
  done: { dot: "bg-emerald-500", text: "text-emerald-800", bg: "bg-emerald-50", border: "border-emerald-400", Icon: Check },
  blocked: { dot: "bg-red-500", text: "text-red-800", bg: "bg-red-50", border: "border-red-300", Icon: Lock },
};

function PhasePill({ phase, status, index, onCycle }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = phase.icon;
  return (
    <button
      type="button"
      onClick={() => onCycle(phase.key)}
      className={`flex-1 min-w-0 flex items-center gap-2.5 px-3 py-2.5 rounded-md border transition hover:ring-2 hover:ring-norea-primary/30 ${style.bg} ${style.border}`}
      data-testid={`workflow-phase-${phase.key}`}
      title="Cliquer pour avancer : à faire → en cours → terminé"
    >
      <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${style.dot} text-white`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className="text-left min-w-0">
        <div className={`text-[10px] uppercase tracking-wider font-semibold ${style.text}`}>
          Phase {index + 1}
        </div>
        <div className={`text-sm font-medium truncate ${style.text}`}>{phase.label}</div>
      </span>
    </button>
  );
}

export default function DossierHeaderSticky({ dossier, onDossierUpdated }) {
  const ex = dossier.extraction || {};
  const [phases, setPhases] = useState(() => ({ ...(dossier.workflow_phases || {}) }));
  const [editing, setEditing] = useState(false);
  const [partenaires, setPartenaires] = useState([]);
  const [form, setForm] = useState({
    regie: dossier.regie || "",
    partenaire_financier: dossier.partenaire_financier || "",
    sous_traitant: dossier.sous_traitant || "",
    montant_dossier: dossier.montant_dossier ?? (ex.prix_vente || ""),
    departement: dossier.departement || (ex.code_postal ? String(ex.code_postal).slice(0, 2) : ""),
  });

  useEffect(() => {
    setPhases({ ...(dossier.workflow_phases || {}) });
    setForm({
      regie: dossier.regie || "",
      partenaire_financier: dossier.partenaire_financier || "",
      sous_traitant: dossier.sous_traitant || "",
      montant_dossier: dossier.montant_dossier ?? (ex.prix_vente || ""),
      departement: dossier.departement || (ex.code_postal ? String(ex.code_postal).slice(0, 2) : ""),
    });
  }, [dossier.id, dossier.workflow_phases, dossier.regie, dossier.partenaire_financier, dossier.sous_traitant, dossier.montant_dossier, dossier.departement, ex.prix_vente, ex.code_postal]);

  useEffect(() => {
    if (!editing) return;
    api.get("/configuration/lists/partenaires-financiers").then((r) => setPartenaires(r.data.items || [])).catch(() => {});
  }, [editing]);

  const cyclePhase = async (phaseKey) => {
    const current = phases[phaseKey] || "pending";
    const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
    setPhases((p) => ({ ...p, [phaseKey]: next }));
    try {
      await api.post(`/dossiers/${dossier.id}/workflow-phase`, { phase: phaseKey, status: next });
      onDossierUpdated?.();
    } catch {
      // rollback silently
      setPhases((p) => ({ ...p, [phaseKey]: current }));
    }
  };

  const saveFields = async () => {
    const payload = {
      partenaire_financier: form.partenaire_financier || null,
      departement: form.departement || null,
    };
    await api.patch(`/dossiers/${dossier.id}/phase-fields`, payload);
    setEditing(false);
    onDossierUpdated?.();
  };

  const summaryItems = useMemo(() => ([
    { label: "Nom", value: ex.nom || "—" },
    { label: "Prénom", value: ex.prenom || "—" },
    { label: "Département", value: form.departement || "—" },
    { label: "Produit", value: dossier.produit || ex.produit || "—" },
    { label: "Partenaire financier", value: form.partenaire_financier || "—", missing: !form.partenaire_financier },
  ]), [ex.nom, ex.prenom, ex.produit, dossier.produit, form.departement, form.partenaire_financier]);

  return (
    <div
      className="sticky top-0 z-30 -mx-6 px-6 py-3 bg-norea-bg/95 backdrop-blur border-b border-norea-border space-y-3"
      data-testid="dossier-header-sticky"
    >
      {/* Block 1 — Données client */}
      <div className="card-flat px-5 py-3 flex items-center gap-6 flex-wrap" data-testid="header-client-card">
        <div className="overline flex-shrink-0">Données client</div>
        <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-x-6 gap-y-2">
          {summaryItems.map((it) => (
            <div key={it.label} className="flex flex-col min-w-0" data-testid={`summary-${it.label}`}>
              <span className="text-[9px] uppercase tracking-wider text-norea-textMuted truncate">{it.label}</span>
              <span className={`text-sm font-medium truncate ${it.missing ? "text-amber-700" : "text-norea-text"}`}>
                {it.value}
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge status={dossier.status} testid="dossier-detail-status-sticky" />
          <CrmStatusSelect dossier={dossier} onChanged={onDossierUpdated} />
          <button
            onClick={() => setEditing((e) => !e)}
            className="text-xs px-2.5 py-1 rounded border border-norea-border hover:bg-norea-surfaceHover"
            data-testid="btn-edit-phase-fields"
          >
            {editing ? "Fermer" : "Modifier"}
          </button>
        </div>
      </div>

      <EquipeBlock dossier={dossier} onUpdated={onDossierUpdated} />

      <div className="-mt-1 px-1" data-testid="header-tags-row">
        <DossierTagsEditor dossierId={dossier.id} ids={dossier.tags} onChange={onDossierUpdated} />
      </div>

      {/* Edit form (inline) */}
      {editing && (
        <div className="p-3 rounded border border-norea-border bg-white grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="phase-fields-form">
          <SelectWithFree label="Partenaire financier" items={partenaires} value={form.partenaire_financier} onChange={(v) => setForm({ ...form, partenaire_financier: v })} testid="input-partenaire-financier" />
          <label className="flex flex-col text-xs text-norea-textMuted">
            <span className="mb-1">Département</span>
            <input value={form.departement} onChange={(e) => setForm({ ...form, departement: e.target.value })} className="px-2 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary focus:border-norea-primary outline-none" data-testid="input-departement" />
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button onClick={saveFields} className="btn-primary text-xs" data-testid="btn-save-phase-fields">Enregistrer</button>
          </div>
        </div>
      )}

      {/* Block 2 — Process en 5 phases */}
      <div className="card-flat px-5 py-3" data-testid="header-process-card">
        <div className="overline mb-2">Process du dossier</div>
        <div className="flex items-center gap-2" data-testid="workflow-horizontal">
          {PHASES.map((p, i) => (
            <React.Fragment key={p.key}>
              <PhasePill phase={p} status={phases[p.key] || "pending"} index={i} onCycle={cyclePhase} />
              {i < PHASES.length - 1 && (
                <div className="w-4 h-0.5 bg-norea-border flex-shrink-0" aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function SelectWithFree({ label, items, value, onChange, testid }) {
  // Combo-box: pick from list OR type freely
  return (
    <label className="flex flex-col text-xs text-norea-textMuted">
      <span className="mb-1">{label}</span>
      <input
        list={`${testid}-list`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary focus:border-norea-primary outline-none"
        placeholder="Choisir ou saisir…"
        data-testid={testid}
      />
      <datalist id={`${testid}-list`}>
        {items.map((it) => <option key={it.id} value={it.name} />)}
      </datalist>
    </label>
  );
}
